import { Box, Button, Card, Group, Paper, ScrollArea, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import React, { useCallback, useMemo, useState } from 'react';

export interface RelationshipNode {
  id: string;           // unique id (type:key)
  key: string;          // key/id within kind
  kind: string;         // WorkflowDefinition, Instance, Task, etc.
  name?: string;        // human name if available
  type?: string;        // entity type
}

export interface RelationshipEdge {
  from: string;         // node.id
  to: string;           // node.id
  reason: string;       // why linked
}

export interface RelationshipInspectorProps {
  tenant: string;
  selected: { id: string; key: string; type?: string; name?: string; draft?: Record<string, unknown> };
  rows: Array<{ id: string; key: string; type?: string; name?: string; draft?: Record<string, unknown> }>;
  onHydrateAll?: () => Promise<void>;  // ensure drafts are loaded for rows
}

function safeJSON(obj: unknown): string {
  try { return JSON.stringify(obj ?? {}); } catch { return '{}'; }
}

function includesToken(haystack: string, token: string | undefined): boolean {
  if (!token) return false;
  try { return haystack.toLowerCase().includes(String(token).toLowerCase()); } catch { return false; }
}

/**
 * Heuristic relationship resolver:
 * - direct reference by key/name inside draft blobs (both directions)
 * - instance to definition via common name fields
 * - workflow to child invocations via action/metadata hints
 */
function resolveRelationships(selected: RelationshipNode & { draft?: any }, nodes: Array<RelationshipNode & { draft?: any }>): RelationshipEdge[] {
  const edges: RelationshipEdge[] = [];
  const selBlob = safeJSON(selected.draft);
  const selKey = selected.key;
  const selName = selected.name;

  for (const other of nodes) {
    if (other.id === selected.id) continue;
    const otherBlob = safeJSON(other.draft);

    // A) Cross‑draft substring matching (both directions)
    if (includesToken(otherBlob, selKey) || includesToken(otherBlob, selName)) {
      edges.push({ from: other.id, to: selected.id, reason: `references ${selected.type || selected.kind}` });
    }
    if (includesToken(selBlob, other.key) || includesToken(selBlob, other.name)) {
      edges.push({ from: selected.id, to: other.id, reason: `references ${other.type || other.kind}` });
    }

    // B) Instance ↔ Definition by name (common pattern)
    if ((selected.type === 'Instance' || selected.kind === 'Instance') && (other.type === 'WorkflowDefinition' || other.kind === 'WorkflowDefinition')) {
      if (includesToken(selBlob, other.key) || includesToken(selBlob, other.name) || includesToken(otherBlob, selName)) {
        edges.push({ from: selected.id, to: other.id, reason: 'instance_of' });
      }
    }
    if ((selected.type === 'WorkflowDefinition' || selected.kind === 'WorkflowDefinition') && (other.type === 'Instance' || other.kind === 'Instance')) {
      if (includesToken(otherBlob, selected.key) || includesToken(otherBlob, selected.name) || includesToken(selBlob, other.name)) {
        edges.push({ from: other.id, to: selected.id, reason: 'instance_of' });
      }
    }

    // C) Workflow invokes sub‑workflows via actions (heuristic keys)
    if (selected.type === 'WorkflowDefinition' && other.type === 'WorkflowDefinition') {
      if (includesToken(selBlob, 'invoke') && (includesToken(selBlob, other.key) || includesToken(selBlob, other.name))) {
        edges.push({ from: selected.id, to: other.id, reason: 'invokes' });
      }
    }
  }
  // Deduplicate edges by from+to+reason
  const seen = new Set<string>();
  return edges.filter(e => { const k = `${e.from}|${e.to}|${e.reason}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

export const RelationshipInspector: React.FC<RelationshipInspectorProps> = ({ tenant, selected, rows, onHydrateAll }) => {
  const [view, setView] = useState<'table' | 'graph'>('table');
  const [resolving, setResolving] = useState(false);

  const nodes = useMemo<Array<RelationshipNode & { draft?: any }>>(() => rows.map(r => ({
    id: r.id,
    key: r.key,
    kind: r.type || 'Unknown',
    name: r.name || r.key,
    draft: r.draft || {}
  })), [rows]);

  const selectedNode = useMemo(() => nodes.find(n => n.id === selected.id) || ({ ...selected, kind: selected.type || 'Unknown' } as any), [nodes, selected]);

  const edges = useMemo<RelationshipEdge[]>(() => resolveRelationships(selectedNode as any, nodes), [selectedNode, nodes]);

  const hydrateThenResolve = useCallback(async () => {
    if (!onHydrateAll) return;
    setResolving(true);
    try { await onHydrateAll(); } finally { setResolving(false); }
  }, [onHydrateAll]);

  // Simple layout: group nodes by kind into columns
  const kinds = useMemo(() => {
    const set = new Set<string>(nodes.map(n => n.kind ?? 'Unknown').filter(Boolean));
    return Array.from(set).sort();
  }, [nodes]);

  const positions = useMemo(() => {
    const colWidth = 240; const rowHeight = 64; const margin = 24;
    const pos: Record<string, { x: number; y: number; color: string }> = {};
    const colorFor = (kind: string | undefined): string => {
      if (!kind) return '#666666'; // Default color for undefined
      const idx = Math.abs(kind.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 10;
      const palette = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0ea5e9', '#059669', '#d97706', '#ea580c', '#6d28d9'];
      return palette[idx] ?? '#666666';
    };
    kinds.forEach((k, ci) => {
      if (!k) return; // Skip undefined kinds
      const kind: string = k; // Type narrowing
      const ofKind = nodes.filter(n => n.kind === kind);
      ofKind.forEach((n, ri) => {
        const nodeKind: string = n.kind ?? 'Unknown'; // Handle potential undefined
        const nodeId: string = n.id ?? `node-${ci}-${ri}`; // Handle potential undefined id
        pos[nodeId] = { x: margin + ci * colWidth, y: margin + ri * rowHeight, color: colorFor(nodeKind) };
      });
    });
    return pos;
  }, [nodes, kinds]);

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Title order={5}>Relationships</Title>
        <Group>
          <Select
            value={view}
            onChange={(v: string | null) => setView((v as 'table' | 'graph') || 'table')}
            data={[{ value: 'table', label: 'Table' }, { value: 'graph', label: 'Graph' }]}
            w={120}
          />
          <Button variant="light" loading={resolving} onClick={hydrateThenResolve}>Find Relationships</Button>
        </Group>
      </Group>

      {view === 'table' ? (
        <ScrollArea h={260}>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>From</Table.Th>
                <Table.Th>→</Table.Th>
                <Table.Th>To</Table.Th>
                <Table.Th>Reason</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {edges.length === 0 && (
                <Table.Tr><Table.Td colSpan={4}><Text c="dimmed">No relationships found. Click “Find Relationships”.</Text></Table.Td></Table.Tr>
              )}
              {edges.map((e, i) => {
                const from = nodes.find(n => n.id === e.from);
                const to = nodes.find(n => n.id === e.to);
                return (
                  <Table.Tr key={`${e.from}-${e.to}-${i}`}>
                    <Table.Td>{`${from?.kind ?? ''}:${from?.name ?? ''}`}</Table.Td>
                    <Table.Td>→</Table.Td>
                    <Table.Td>{`${to?.kind ?? ''}:${to?.name ?? ''}`}</Table.Td>
                    <Table.Td>{e.reason ?? ''}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      ) : (
        <Box style={{ position: 'relative', height: 320, border: '1px solid #e9ecef', borderRadius: 4 }}>
          {/* Edges */}
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
            {edges.map((e, i) => {
              const a = positions[e.from];
              const b = positions[e.to];
              if (!a || !b) return null;
              const x1 = a.x + 160; const y1 = a.y + 24; // right side of from
              const x2 = b.x + 8;   const y2 = b.y + 24; // left side of to
              return (
                <g key={`${e.from}-${e.to}-${i}`}>
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#arrow)" />
                  <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4} fontSize={10} fill="#64748b">{e.reason}</text>
                </g>
              );
            })}
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L0,6 L9,3 z" fill="#94a3b8" />
              </marker>
            </defs>
          </svg>
          {/* Nodes */}
          {nodes.map(n => {
            const pos = positions[n.id];
            if (!pos) return null;
            return (
              <Paper key={n.id} withBorder p="xs" style={{ position: 'absolute', left: pos.x, top: pos.y, width: 160, background: '#fff' }}>
                <Stack gap={2}>
                  <Group gap={6}>
                    <Box style={{ width: 8, height: 8, borderRadius: 2, background: pos.color }} />
                    <Text size="xs" c="dimmed">{n.kind}</Text>
                  </Group>
                  <Tooltip label={n.name}>
                    <Text size="sm" fw={600} lineClamp={1}>{n.name}</Text>
                  </Tooltip>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}
      <Text size="xs" c="dimmed" mt="xs">Tenant: {tenant}</Text>
    </Card>
  );
};

export default RelationshipInspector;


